import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, AlertTriangle, Search, ChevronDown, ChevronRight, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { Agent, Model, OutputType, CreateAgentForm, CreateModelForm } from '../../types';
import ModelAttachmentSystem from './ModelAttachmentSystem';
import ModelFormModal from '../models/ModelFormModal';
import ModelSchemaPreview from './ModelSchemaPreview';
import InlineModelCreator from './InlineModelCreator';
import ToolManagementSystem from './ToolManagementSystem';
import GoDaddyToolSelector from './GoDaddyToolSelector';
import RDAPToolSelector from './RDAPToolSelector';
import RegistryToolSelector from './RegistryToolSelector';
import { useProjectStore } from '../../store';
import Modal from '../common/Modal';
import EntityHistoryTab from '../history/EntityHistoryTab';
import HistoryDropdown from '../history/HistoryDropdown';
import ContextualHelpIcon from '../help/ContextualHelpIcon';
import CustomSelect from '../common/CustomSelect';
import ModelPickerField from '../common/ModelPickerField';
import InstructionAssistantButton from './InstructionAssistantButton';
import InstructionAssistantPanel from './InstructionAssistantPanel';
import { useAgents } from '../../hooks/queries/useAgents';
import { useModels } from '../../hooks/queries/useModels';
import { useUpdateModel } from '../../hooks/queries/useModelMutations';

const FOCUS_FIELD_HIGHLIGHT_MS = 2000;

interface AgentFormModalProps {
  agent?: Agent | null;
  models: Model[];
  availableAgents?: Agent[];
  templateData?: Partial<CreateAgentForm> | null;
  stepIntent?: string;
  upstreamModelId?: number;
  onSubmit: (data: CreateAgentForm) => Promise<void>;
  onClose: () => void;
  showToast?: boolean;
  closeOnSubmit?: boolean;
  focusField?: 'model';
}

interface FormData extends CreateAgentForm {
  modelParameters: Record<string, any>;
}

interface ValidationErrors {
  name?: string;
  instructions?: string;
  model?: string;
  output_model_id?: string;
  submit?: string;
}

const AgentFormModal: React.FC<AgentFormModalProps> = ({ agent, models, availableAgents = [], templateData, stepIntent, upstreamModelId, onSubmit, onClose, showToast = true, closeOnSubmit = true, focusField }) => {
  // AC-1411 / AC-1377 P4.5: Read agents and models through the canonical
  // RQ hooks. The `models` prop and `availableAgents` prop are kept for
  // back-compat with consumers that still pass them in (e.g.
  // ToolManagementSystem opens this modal with model[] from the store
  // snapshot at click time); the hooks supplement those props with the
  // live cache so opening the modal after a sibling mutation reflects the
  // fresh state. The `models` prop is also still respected because some
  // callers (e.g. step builders) pre-filter the list before passing it in.
  //
  // Schema editing is now routed through `useUpdateModel`, which
  // invalidates both `projectModels` and `projectRegistries` per the
  // AC-1408 dual-key contract.
  // AC-1547 / AC-1549 (epic AC-1537): the BYOK-gated, grouped-by-provider
  // model picker (AC-1516) lives in <ModelPickerField> now, and the
  // underpowered-model warning (AC-1348) moved to StepConfigurationModal.
  // This component no longer reads `llmCatalog` directly — the picker
  // pulls it from the store internally.
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.project_id;
  const agentsQuery = useAgents(projectId);
  const modelsQuery = useModels(projectId);
  const updateModelMutation = useUpdateModel(projectId ?? '');

  // Use RQ-cached agents if available, then store snapshot, then prop.
  const agents = agentsQuery.data ?? [];
  // Use store agents if available, otherwise fall back to prop
  const availableAgentsForTools = agents.length > 0 ? agents : availableAgents;

  const [formData, setFormData] = useState<FormData>({
    name: '',
    instructions: '',
    model: 'gpt-4o-mini',
    model_settings: {},
    output_type: 'str' as OutputType,
    output_model_id: undefined,
    tools: [],
    modelParameters: {}
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loading, setLoading] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [showModelPreview, setShowModelPreview] = useState(false);
  const [selectedModelForPreview, setSelectedModelForPreview] = useState<Model | null>(null);
  const [showModelSelectionDropdown, setShowModelSelectionDropdown] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    output: true,
    advanced: focusField === 'model',
    tools: false
  });

  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const [modelHighlight, setModelHighlight] = useState(focusField === 'model');

  useEffect(() => {
    if (focusField !== 'model') return;
    const node = modelDropdownRef.current;
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const t = setTimeout(() => setModelHighlight(false), FOCUS_FIELD_HIGHLIGHT_MS);
    return () => clearTimeout(t);
  }, [focusField]);

  // New state for inline model creation
  const [showInlineModelCreator, setShowInlineModelCreator] = useState(false);
  const [modelCreationMode, setModelCreationMode] = useState<'select' | 'create'>('select');
  const [localModels, setLocalModels] = useState<Model[]>(models);
  const [isCreatingModel, setIsCreatingModel] = useState(false);

  // Tab state for switching between edit form and history
  const [activeTab, setActiveTab] = useState<'edit' | 'history'>('edit');

  // Instruction Assistant panel state (AC-1192)
  const [showInstructionAssistant, setShowInstructionAssistant] = useState(false);

  // AC-1549 (epic AC-1537): the underpowered-model warning previously
  // owned by this modal moved to `StepConfigurationModal`, where it can
  // read the effective step model (`step.model OR agent.model`). The
  // `warningDismissed` state was removed alongside the banner.

  // Inline schema editing from the Output Schema gear icon (AC-1207)
  const [editingSchema, setEditingSchema] = useState<Model | null>(null);

  // AC-1411: `useModels` already auto-fetches when projectId is truthy and
  // the cache is empty - no manual seed effect needed. We only force a
  // refetch via `modelsQuery.refetch()` from explicit user actions
  // (refresh button below).

  // Initialize form data when agent prop changes
  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name || '',
        instructions: agent.instructions || '',
        model: agent.model || 'gpt-4o-mini',
        model_settings: agent.model_settings || {},
        output_type: agent.output_type || 'str',
        output_model_id: agent.output_model_id || undefined,
        tools: agent.tools || [],
        modelParameters: {}
      });

      // Ensure the agent's output model is available in local models
      if (agent.output_model_id) {
        // First try to use the agent's output_model if available
        if (agent.output_model) {
          setLocalModels(prev => {
            const existingModel = prev.find(m => m.model_id === agent.output_model_id);
            if (existingModel) {
              return prev;
            }
            return [...prev, agent.output_model!];
          });
        } else {
          // If output_model is not available, try to fetch it from the API
          const fetchMissingModel = async () => {
            try {
              const apiService = (await import('../../services/api')).default;
              const specificModel = await apiService.getModel(agent.output_model_id!);
              setLocalModels(prev => {
                const existingModel = prev.find(m => m.model_id === specificModel.model_id);
                if (existingModel) {
                  return prev;
                }
                return [...prev, specificModel];
              });
            } catch (error) {
              console.error('[AgentFormModal] Failed to fetch agent output model:', error);
              // Model might have been deleted, but we'll let the refresh button handle this
            }
          };
          fetchMissingModel();
        }
      } else {
      }
    }
  }, [agent]);

  // Initialize form data from template
  useEffect(() => {
    if (templateData && !agent) {
      setFormData({
        name: templateData.name || '',
        instructions: templateData.instructions || '',
        model: templateData.model || 'gpt-4o-mini',
        model_settings: templateData.model_settings || {},
        output_type: templateData.output_type || 'str',
        output_model_id: templateData.output_model_id || undefined,
        tools: templateData.tools || [],
        modelParameters: {}
      });
    }
  }, [templateData, agent]);

  // Additional useEffect to handle missing models when localModels changes
  useEffect(() => {
    if (formData.output_model_id && localModels.length > 0) {
      const modelExists = localModels.some(m => m.model_id === formData.output_model_id);
      if (!modelExists) {
        
        const fetchMissingModel = async () => {
          try {
            const apiService = (await import('../../services/api')).default;
            const model = await apiService.getModel(formData.output_model_id!);
            setLocalModels(prev => {
              const exists = prev.some(m => m.model_id === model.model_id);
              if (!exists) {
                return [...prev, model];
              }
              return prev;
            });
          } catch (error) {
            console.error('[AgentFormModal] Failed to fetch missing model via secondary check:', error);
          }
        };
        
        fetchMissingModel();
      }
    }
  }, [formData.output_model_id, localModels]);

  // Update output_model_id when output type changes
  useEffect(() => {
    // Only clear output_model_id if output_type actually changed to something other than 'model'
    if (formData.output_type !== 'model' && formData.output_model_id !== undefined) {
      setFormData(prev => ({
        ...prev,
        output_model_id: undefined
      }));
    }
  }, [formData.output_type]);

  // Update local models when models prop changes
  useEffect(() => {
    
    // Don't overwrite localModels if we're currently creating a model
    // This prevents duplicate models from appearing when the global store updates
    if (!isCreatingModel) {
      setLocalModels(prev => {
        // Preserve any agent-specific models that might not be in the global models list
        const agentModels = prev.filter(m => 
          agent?.output_model_id === m.model_id && 
          !models.some(globalModel => globalModel.model_id === m.model_id)
        );
        
        // Combine global models with preserved agent models
        const newLocalModels = [...models, ...agentModels];
        return newLocalModels;
      });
    }
  }, [models, isCreatingModel, agent?.output_model_id]);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Agent name is required';
    }

    if (!formData.instructions.trim()) {
      newErrors.instructions = 'Instructions are required';
    }

    if (!formData.model) {
      newErrors.model = 'Model is required';
    }

    if (formData.output_type === 'model' && !formData.output_model_id) {
      newErrors.output_model_id = 'Output schema is required when output type is structured data';
    }



    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent bubbling to parent modals
    
    
    // Prevent form submission if we're currently creating a model or if the model creator is expanded
    if (isCreatingModel || showInlineModelCreator) {
      return;
    }

    // Check if this modal should be allowed to submit
    // Only the topmost modal in the stack should be able to submit
    const activeModals = document.querySelectorAll('[data-modal-active="true"]');
    const thisModalElement = e.currentTarget.closest('[data-modal-active="true"]');
    
    if (activeModals.length > 1 && !thisModalElement) {
      return;
    }
    
    if (activeModals.length > 0 && !thisModalElement) {
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitData: CreateAgentForm = {
        name: formData.name.trim(),
        instructions: formData.instructions.trim(),
        model: formData.model,
        model_settings: formData.model_settings,
        output_type: formData.output_type,
        output_model_id: formData.output_type === 'model' ? formData.output_model_id : null,
        tools: formData.tools
      };

      await onSubmit(submitData);
      
      // Show success message if enabled
      if (showToast) {
        toast.success(agent ? 'Agent updated successfully!' : 'Agent created successfully!');
      }
      // Close the modal if enabled
      if (closeOnSubmit) {
        onClose();
      } else {
      }
    } catch (error: any) {
      console.error('[AgentFormModal] Error submitting form:', error);
      // Handle unique constraint violation
      if (error.response?.data?.detail?.includes('UNIQUE constraint failed')) {
        setErrors(prev => ({
          ...prev,
          name: 'An agent with this name already exists in this project'
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          submit: 'Failed to save agent. Please try again.'
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    
    // Handle output_type changes
    if (field === 'output_type') {
      setFormData(prev => {
        const newData = { ...prev, [field]: value };
        
        // If changing to 'model' output type and no model is currently selected,
        // ensure output_model_id is undefined (not null) to show empty state
        if (value === 'model' && !prev.output_model_id) {
          newData.output_model_id = undefined;
        }
        // If changing away from 'model' output type, clear the model selection
        else if (value !== 'model' && prev.output_model_id) {
          newData.output_model_id = undefined;
        }
        
        return newData;
      });
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleToolChoiceChange = (value: string) => {
    const newSettings = { ...(formData.model_settings || {}) };
    if (value === 'auto') {
      delete newSettings.tool_choice;
    } else {
      newSettings.tool_choice = value;
    }
    handleInputChange('model_settings', newSettings);
  };

  const currentToolChoice: string = (formData.model_settings as Record<string, any>)?.tool_choice || 'auto';

  // AC-1549 (epic AC-1537): the underpowered-model warning was removed
  // from this modal because step.model now drives runtime selection. The
  // warning lives in StepConfigurationModal and reads the effective
  // step model. The complexity heuristic still exists at
  // `frontend/src/utils/agentComplexity.ts` and is consumed by
  // StepConfigurationModal directly.

  // AC-1547 (epic AC-1537): the model-picker option building + BYOK CTA
  // logic that used to live here (AC-1516 / AC-1517) is now encapsulated
  // inside <ModelPickerField>. This component just renders the field with
  // the agent's `formData.model` and lets the field handle the grouping,
  // disabled-row state, missing-key CTA, and the listApiKeys fetch.

  const handleModelPreview = (model: Model) => {
    setSelectedModelForPreview(model);
    setShowModelPreview(true);
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  /**
   * AC-1446: Shared "create or reuse Models from a list of suggested schemas
   * and bind the last one as the agent's output model" helper. Used by:
   *   - InstructionAssistantPanel.onCreateSchema (existing caller)
   *   - RDAPToolSelector.onUseRecommendedSchemas (this ticket)
   *
   * Behavior matches the original inline closure exactly: for each schema,
   * reuse an existing Model with the same `class_name` if present, otherwise
   * create a new one; then set `output_type='model'` and `output_model_id`
   * to the last created/reused Model. Surfaces a toast summarizing what
   * happened. The flow is idempotent: re-running with the same schemas
   * reuses both rows and re-attaches the wrapper without creating
   * duplicates (matches the existing `class_name`-based dedupe contract).
   */
  const applyRecommendedSchemas = async (
    schemas: Array<{
      class_name: string;
      // Optional to match `SuggestedSchema` from the InstructionAssistant
      // pathway. The RDAP banner always supplies a description so callers
      // that strictly type to a non-optional `description` are still
      // assignable to this looser shape.
      description?: string;
      properties: Record<string, any>;
    }>
  ): Promise<void> => {
    if (!projectId) return;
    if (!schemas || schemas.length === 0) return;
    try {
      const apiService = (await import('../../services/api')).default;
      const resolvedModels: Model[] = [];
      const createdNames: string[] = [];
      const reusedNames: string[] = [];
      const newlyCreated: Model[] = [];

      for (const schema of schemas) {
        const existing = localModels.find(
          (m) => m.class_name === schema.class_name
        );
        if (existing) {
          resolvedModels.push(existing);
          reusedNames.push(schema.class_name);
        } else {
          const newModel = await apiService.createModel(String(projectId), {
            class_name: schema.class_name,
            description: schema.description || '',
            properties: schema.properties,
          });
          resolvedModels.push(newModel);
          newlyCreated.push(newModel);
          createdNames.push(schema.class_name);
        }
      }

      if (newlyCreated.length > 0) {
        setLocalModels((prev) => [...prev, ...newlyCreated]);
      }
      // The last schema is the top-level output (contractual ordering).
      const outputModel = resolvedModels[resolvedModels.length - 1];
      setFormData((prev) => ({
        ...prev,
        output_type: 'model' as any,
        output_model_id: outputModel.model_id,
      }));
      setExpandedSections((prev) => ({ ...prev, output: true }));
      setModelCreationMode('select');
      setShowInlineModelCreator(false);

      const parts: string[] = [];
      if (createdNames.length > 0) {
        parts.push(`created ${createdNames.join(', ')}`);
      }
      if (reusedNames.length > 0) {
        parts.push(`reused existing ${reusedNames.join(', ')}`);
      }
      const action = parts.join(' and ');
      toast.success(
        `Schema${resolvedModels.length > 1 ? 's' : ''}: ${action}. Attached "${outputModel.class_name}" to agent.`
      );
    } catch (err: any) {
      console.error('Failed to create schema:', err);
      const detail =
        err?.response?.data?.detail ||
        'Please check the schema and try again.';
      toast.error(`Failed to create schema: ${detail}`);
    }
  };

  const handleRefreshModels = async () => {
    if (!currentProject) return;
    
    setLoadingModels(true);
    try {
      // Re-fetch models from the API via the canonical RQ cache.
      await modelsQuery.refetch();
      // The models will be updated via the useEffect that watches the models prop
      
      // If we have an agent with an output_model_id but the model is missing, try to fetch it specifically
      if (agent?.output_model_id && !localModels.find(m => m.model_id === agent.output_model_id)) {
        try {
          const apiService = (await import('../../services/api')).default;
          const specificModel = await apiService.getModel(agent.output_model_id);
          setLocalModels(prev => {
            const existingModel = prev.find(m => m.model_id === specificModel.model_id);
            if (existingModel) {
              return prev;
            }
            return [...prev, specificModel];
          });
        } catch (modelError) {
          console.error('[AgentFormModal] Failed to fetch specific model:', modelError);
        }
      }
      
      // Also check if formData has an output_model_id that's missing
      if (formData.output_model_id && formData.output_model_id !== agent?.output_model_id) {
        const modelExists = localModels.some(m => m.model_id === formData.output_model_id);
        if (!modelExists) {
          try {
            const apiService = (await import('../../services/api')).default;
            const specificModel = await apiService.getModel(formData.output_model_id);
            setLocalModels(prev => {
              const exists = prev.some(m => m.model_id === specificModel.model_id);
              if (!exists) {
                return [...prev, specificModel];
              }
              return prev;
            });
          } catch (modelError) {
            console.error('[AgentFormModal] Failed to fetch specific formData model:', modelError);
          }
        }
      }
    } catch (error) {
      console.error('[AgentFormModal] Failed to refresh models:', error);
    } finally {
      setLoadingModels(false);
    }
  };

  // Handler for switching to create mode
  const handleSwitchToCreateMode = () => {
    setModelCreationMode('create');
    setShowInlineModelCreator(true);
  };

  // Handler for switching to select mode
  const handleSwitchToSelectMode = () => {
    setModelCreationMode('select');
    setShowInlineModelCreator(false);
    // Automatically show the model selection dropdown
    setShowModelSelectionDropdown(true);
  };

  // Handler for when a new model is created inline
  const handleModelCreated = async (newModel: Model) => {
    setIsCreatingModel(true);
    
    try {
      // Add the new model to local state immediately to prevent "Unknown Model" issue
      setLocalModels(prev => {
        const existingModel = prev.find(m => m.model_id === newModel.model_id);
        if (existingModel) {
          return prev;
        }
        const newModels = [...prev, newModel];
        return newModels;
      });

      // Set the newly created model as the selected output model immediately
      setFormData(prev => {
        const newFormData = {
          ...prev,
          output_model_id: newModel.model_id
        };
        return newFormData;
      });
      
      // Switch back to select mode
      setModelCreationMode('select');
      setShowInlineModelCreator(false);
      
      // Clear any output model errors
      if (errors.output_model_id) {
        setErrors(prev => ({ ...prev, output_model_id: undefined }));
      }

      // Show success message
      toast.success(`Model "${newModel.class_name}" created and selected successfully!`);
      
    } catch (error) {
      console.error('[AgentFormModal] Error handling model creation:', error);
      toast.error('Model created but there was an issue updating the form. Please refresh and try again.');
    } finally {
      setIsCreatingModel(false);
    }
  };

  // Handler for canceling inline model creation
  const handleCancelModelCreation = () => {
    setModelCreationMode('select');
    setShowInlineModelCreator(false);
  };

  // Prevent form submission when model creator is active
  const handleFormKeyDown = (e: React.KeyboardEvent) => {
    if (showInlineModelCreator && e.key === 'Enter') {
      e.preventDefault();
    }
  };

  // ModelAttachmentSystem rendering block
  const modelAttachmentSystemBlock = (
    <ModelAttachmentSystem
      models={localModels}
      attachedModelIds={(() => {
        const ids = formData.output_model_id ? [formData.output_model_id] : [];
        return ids;
      })()}
      onModelAttach={(modelId) => {
        setFormData(prev => ({
          ...prev,
          output_model_id: modelId
        }));
        setShowModelSelectionDropdown(false);
      }}
      onModelDetach={() => {
        setFormData(prev => ({
          ...prev,
          output_model_id: undefined
        }));
      }}
      onModelReorder={() => {}} // Not used for single model
      onModelPreview={handleModelPreview}
      onModelConfigure={(model) => setEditingSchema(model)}
      maxAttachments={1}
      allowReordering={false}
      isLoadingModels={loadingModels}
      onRefreshModels={handleRefreshModels}
      showAvailableModels={showModelSelectionDropdown}
      onToggleAvailableModels={setShowModelSelectionDropdown}
    />
  );

  const handleInlineSchemaSave = async (data: CreateModelForm, editId?: number) => {
    // Save the schema edit via the RQ mutation hook so localModels and the
    // RQ cache (projectModels + projectRegistries) stay in sync.
    if (!editId) {
      setEditingSchema(null);
      return;
    }
    try {
      const updated = await updateModelMutation.mutateAsync({ modelId: editId, data });
      if (updated) {
        // Update localModels with the edited schema so the form reflects changes immediately
        setLocalModels(prev => prev.map(m => m.model_id === updated.model_id ? updated : m));
        toast.success(`Schema "${updated.class_name}" updated`);
      }
      setEditingSchema(null);
    } catch (err) {
      console.error('[AgentFormModal] Failed to update schema:', err);
      toast.error('Failed to update schema');
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-modal-active="true">
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" data-tutorial="form-agent">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white">
                {agent ? 'Edit Agent' : 'Create New Agent'}
              </h2>
              <ContextualHelpIcon
                docPath="features/agents.md"
                tooltip="Learn about agents"
                size="sm"
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Project: {currentProject?.name || 'Unknown Project'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* History dropdown - only for existing agents */}
            {agent?.agent_id && projectId && (
              <HistoryDropdown
                entityType="agent"
                entityId={agent.agent_id}
                projectId={parseInt(String(projectId), 10)}
                onHistoryClick={() => setActiveTab('history')}
              />
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'edit'}
              onClick={() => setActiveTab('edit')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'edit'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              Edit
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'history'}
              onClick={() => setActiveTab('history')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'history'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              History
            </button>
          </nav>
        </div>

        {/* History Tab Content */}
        {activeTab === 'history' && (
          <div className="flex-1 overflow-y-auto p-6">
            <EntityHistoryTab
              entityType="agent"
              entityId={agent?.agent_id || null}
              projectId={projectId ? parseInt(String(projectId), 10) : 0}
            />
          </div>
        )}

        {/* Form - shown when Edit tab is active */}
        {activeTab === 'edit' && (
        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="flex flex-col flex-1 min-h-0" autoComplete="off">
          {/* AC-1549 (epic AC-1537): the underpowered-model warning that
              used to render here moved to StepConfigurationModal so it
              can read the effective step model. */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => toggleSection('basic')}
                className="flex items-center w-full text-left"
              >
                {expandedSections.basic ? (
                  <ChevronDown className="w-5 h-5 mr-2" />
                ) : (
                  <ChevronRight className="w-5 h-5 mr-2" />
                )}
                <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Basic Information</h3>
              </button>

              {expandedSections.basic && (
                <div className="pl-7 space-y-4">
                  <div>
                    <label htmlFor="agent-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Agent Name *
                    </label>
                    <input
                      id="agent-name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-warm-white ${
                        errors.name ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Enter agent name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="agent-instructions" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Instructions *
                      </label>
                      <InstructionAssistantButton
                        agentName={formData.name}
                        currentInstructions={formData.instructions}
                        onOpenPanel={() => setShowInstructionAssistant(true)}
                        disabled={!formData.name.trim()}
                      />
                    </div>
                    <textarea
                      id="agent-instructions"
                      value={formData.instructions}
                      onChange={(e) => handleInputChange('instructions', e.target.value)}
                      rows={12}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y dark:bg-gray-800 dark:text-warm-white ${
                        errors.instructions ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Enter detailed instructions for the agent"
                      data-tutorial="agent-instructions"
                    />
                    {errors.instructions && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        {errors.instructions}
                      </p>
                    )}
                    {showInstructionAssistant && (
                      <InstructionAssistantPanel
                        agentName={formData.name}
                        currentInstructions={formData.instructions}
                        projectId={projectId ? parseInt(String(projectId), 10) : undefined}
                        stepIntent={stepIntent}
                        models={localModels}
                        outputModelId={formData.output_model_id || undefined}
                        upstreamModelId={upstreamModelId}
                        attachedToolTypes={(formData.tools || []).map(t => t.type)}
                        onAccept={(instructions) => {
                          handleInputChange('instructions', instructions);
                          setShowInstructionAssistant(false);
                        }}
                        onClose={() => setShowInstructionAssistant(false)}
                        onCreateSchema={async (schemas) => {
                          // AC-1446: This closure used to inline the full
                          // create-or-reuse + bind-output flow. It has been
                          // extracted to `applyRecommendedSchemas` above so
                          // RDAPToolSelector can reuse the same pathway. We
                          // pass schemas through unchanged.
                          await applyRecommendedSchemas(schemas);
                        }}
                        onAttachTools={(tools) => {
                          const existingTypes = (formData.tools || []).map(t => t.type);
                          const newToolsToAdd = tools
                            .filter(t => !existingTypes.includes(t.type))
                            .map(t => ({
                              type: t.type as any,
                              name: t.type === 'web_search' ? 'Web Search' : 'Knowledge Base Search',
                              description: t.rationale || '',
                            }));
                          if (newToolsToAdd.length === 0) {
                            toast('All suggested tools are already attached');
                            return;
                          }
                          setFormData(prev => ({
                            ...prev,
                            tools: [...(prev.tools || []), ...newToolsToAdd],
                          }));
                          setExpandedSections(prev => ({ ...prev, tools: true }));
                          const names = newToolsToAdd.map(t => t.name).join(', ');
                          toast.success(`Attached ${newToolsToAdd.length} tool${newToolsToAdd.length > 1 ? 's' : ''}: ${names}`);
                        }}
                      />
                    )}
                  </div>


                </div>
              )}
            </div>

            {/* Response Formatting */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => toggleSection('output')}
                className="flex items-center w-full text-left"
              >
                {expandedSections.output ? (
                  <ChevronDown className="w-5 h-5 mr-2" />
                ) : (
                  <ChevronRight className="w-5 h-5 mr-2" />
                )}
                <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Response Formatting</h3>
              </button>

              {expandedSections.output && (
                <div className="pl-7 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Response Format
                    </label>
                    <CustomSelect
                      value={formData.output_type || 'str'}
                      onChange={(value) => handleInputChange('output_type', value as OutputType)}
                      options={[
                        { value: 'str', label: 'Plain Text' },
                        { value: 'model', label: 'Structured Data (Schema)' },
                      ]}
                      className="w-full"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      How the agent's responses are formatted and structured
                    </p>
                  </div>

                  {formData.output_type === 'model' && (
                    <div>
                      {/* Model Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Output Schema *
                        </label>
                        
                        {/* Model Selection Controls */}
                        <div className="mb-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <button
                              type="button"
                              onClick={handleSwitchToSelectMode}
                              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                modelCreationMode === 'select'
                                  ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-primary-300 dark:border-primary-600'
                                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700'
                              }`}
                            >
                              Choose Existing Schema
                            </button>
                            <button
                              type="button"
                              onClick={handleSwitchToCreateMode}
                              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                modelCreationMode === 'create'
                                  ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-primary-300 dark:border-primary-600'
                                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700'
                              }`}
                            >
                              Create New Schema
                            </button>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {modelCreationMode === 'select'
                              ? 'Select from existing schemas'
                              : 'Define a new schema for agent output'
                            }
                          </div>
                        </div>

                        {/* Select Mode */}
                        {modelCreationMode === 'select' && (
                          <div>
                            {formData.output_model_id && (
                              <div className="mb-3 p-2 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
                                <p className="text-sm text-green-700 dark:text-green-400">
                                  <span className="font-medium">Selected Model:</span> {
                                    localModels.find(m => m.model_id === formData.output_model_id)?.class_name || 'Loading...'
                                  }
                                </p>
                              </div>
                            )}
                            {modelAttachmentSystemBlock}
                            {/* Inline Model Schema Preview Panel */}
                            {showModelPreview && selectedModelForPreview && (
                              <div className="mt-4">
                                <ModelSchemaPreview
                                  model={selectedModelForPreview}
                                  allModels={localModels}
                                  onClose={() => {
                                    setShowModelPreview(false);
                                    setSelectedModelForPreview(null);
                                  }}
                                />
                              </div>
                            )}
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                              Choose an existing data structure model to format and validate agent responses
                            </p>
                          </div>
                        )}

                        {/* Create Mode */}
                        {modelCreationMode === 'create' && (
                          <div>
                            <InlineModelCreator
                              existingModels={localModels}
                              onModelCreated={handleModelCreated}
                              onCancel={handleCancelModelCreation}
                              isExpanded={showInlineModelCreator}
                              onToggleExpanded={() => setShowInlineModelCreator(!showInlineModelCreator)}
                              isCreating={isCreatingModel}
                              projectId={currentProject?.project_id}
                            />
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                              Create a new schema for this agent's output format. The schema will be automatically selected once created.
                            </p>
                          </div>
                        )}

                        {errors.output_model_id && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertTriangle className="w-4 h-4 mr-1" />
                            {errors.output_model_id}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Tools */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => toggleSection('tools')}
                data-tutorial="section-tools"
                className="flex items-center w-full text-left"
              >
                {expandedSections.tools ? (
                  <ChevronDown className="w-5 h-5 mr-2" />
                ) : (
                  <ChevronRight className="w-5 h-5 mr-2" />
                )}
                <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Tools</h3>
              </button>

              {expandedSections.tools && (
                <div className="pl-7 space-y-6">
                  {/* General Tools Section - Always first */}
                  <ToolManagementSystem
                    tools={formData.tools || []}
                    onToolsChange={(tools) => handleInputChange('tools', tools)}
                    availableAgents={availableAgentsForTools}
                    currentAgentId={agent?.agent_id}
                  />

                  {/* Registry Tools Section - Hidden when no models exist */}
                  {currentProject && (
                    <RegistryToolSelector
                      projectId={parseInt(currentProject.project_id, 10)}
                      selectedTools={formData.tools || []}
                      onToolsChange={(tools) => handleInputChange('tools', tools)}
                    />
                  )}

                  {/* GoDaddy Tools Section - Hidden when not configured */}
                  {currentProject && (
                    <GoDaddyToolSelector
                      projectId={parseInt(currentProject.project_id, 10)}
                      selectedTools={formData.tools || []}
                      onToolsChange={(tools) => handleInputChange('tools', tools)}
                    />
                  )}

                  {/* AC-1444 (epic AC-1434): RDAP Tools Section. Credential-free,
                      so no projectId prop -- the selector fetches its tool
                      definitions from /api/v1/tools/rdap/definitions, which is
                      not project-scoped. Rendered alongside GoDaddyToolSelector
                      so the agent author sees both domain-availability options
                      side-by-side (RDAP for free RDAP-based checks, GoDaddy for
                      pricing/purchase).

                      AC-1446: pass `onUseRecommendedSchemas` so the inline
                      "Use recommended schema" banner can route through the
                      same applyRecommendedSchemas helper used by the
                      InstructionAssistant. No new schema-creation pathway. */}
                  <RDAPToolSelector
                    selectedTools={formData.tools || []}
                    onToolsChange={(tools) => handleInputChange('tools', tools)}
                    onUseRecommendedSchemas={applyRecommendedSchemas}
                  />
                </div>
              )}
            </div>

            {/* Advanced Settings */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => toggleSection('advanced')}
                className="flex items-center w-full text-left"
              >
                {expandedSections.advanced ? (
                  <ChevronDown className="w-5 h-5 mr-2" />
                ) : (
                  <ChevronRight className="w-5 h-5 mr-2" />
                )}
                <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Advanced Settings</h3>
              </button>

              {expandedSections.advanced && (
                <div className="pl-7 space-y-4">
                  {/* Model Configuration — AC-1547 (epic AC-1537):
                      relabeled to "Default model for new steps using this
                      agent" because step.model now drives runtime selection;
                      agent.model is the picker default for new steps. The
                      picker itself was extracted into <ModelPickerField>. */}
                  <div
                    ref={modelDropdownRef}
                    className={modelHighlight ? 'rounded ring-2 ring-yellow-400 transition-shadow' : ''}
                  >
                    <ModelPickerField
                      value={formData.model || 'gpt-4o-mini'}
                      onChange={(value) => handleInputChange('model', value)}
                      label="Default model for new steps using this agent"
                      helpText="New orchestration steps that reference this agent will pre-populate with this model. Steps can override the model individually."
                      error={errors.model}
                    />
                  </div>

                  {/* Tool Choice - only shown when agent has tools */}
                  {formData.tools && formData.tools.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tool Choice
                      </label>
                      <CustomSelect
                        value={currentToolChoice}
                        onChange={handleToolChoiceChange}
                        options={[
                          { value: 'auto', label: 'Auto — Model decides when to use tools (default)' },
                          { value: 'required', label: 'Required — Model must call a tool every turn' },
                          { value: 'none', label: 'None — Model will not call tools' },
                        ]}
                        className="w-full"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Controls whether the model must call tools, can choose to, or is prevented from calling them
                      </p>
                    </div>
                  )}

                  {/* Model Settings */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Model Settings (JSON)
                    </label>
                    <textarea
                      value={JSON.stringify(
                        Object.fromEntries(
                          Object.entries(formData.model_settings || {}).filter(([k]) => k !== 'tool_choice')
                        ),
                        null, 2
                      )}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          if ((formData.model_settings as Record<string, any>)?.tool_choice) {
                            parsed.tool_choice = (formData.model_settings as Record<string, any>).tool_choice;
                          }
                          handleInputChange('model_settings', parsed);
                        } catch {
                          // Invalid JSON, keep the text as is for user to fix
                        }
                      }}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                      placeholder='{"temperature": 0.7, "max_tokens": 1000}'
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Advanced settings for the language model (temperature, max tokens, etc.)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            {errors.submit && (
              <p className="text-sm text-red-600 flex items-center mr-auto">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {errors.submit}
              </p>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-tutorial="btn-submit-agent"
              disabled={loading || isCreatingModel || showInlineModelCreator}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : isCreatingModel ? 'Creating Model...' : showInlineModelCreator ? 'Complete Model Creation First' : agent ? 'Update Agent' : 'Create Agent'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
    {/* Inline schema editing modal (AC-1207) */}
    {editingSchema && (
      <ModelFormModal
        open={!!editingSchema}
        onClose={() => setEditingSchema(null)}
        onSave={handleInlineSchemaSave}
        existingModels={localModels}
        editModel={editingSchema}
      />
    )}
    </>
  );
};

export default AgentFormModal;